import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

export default function AirportHotel() {
  const { raceId, userId } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [airportHotel, setAirportHotel] = useState(false);
  const [airportHotelId, setAirportHotelId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [airportList, setAirportList] = useState([]);
  const [hotelDetail, setHotelDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newHotel, setNewHotel] = useState({ name: '', airport: '', address: '', check_in: '', check_out: '' });

  useEffect(() => {
    const token = localStorage.getItem('token');
    // fetch profile
    fetch('/api/profile', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d && d.user) setCurrentUser(d.user); })
      .catch(() => setCurrentUser(null));

    // fetch per-user airport hotel entries for the race
    fetch(`/api/races/${raceId}/airport_hotels`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(list => {
        const found = (list || []).find(n => String(n.user_id) === String(userId));
        if (found) {
          setNote(found);
          setNotesText(found.notes || '');
          setAirportHotel(Boolean(found.airport_hotel_id));
          setAirportHotelId(found.airport_hotel_id || null);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    // fetch list of airport hotels
    fetch('/api/airport_hotels', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(list => setAirportList(list || [])).catch(() => setAirportList([]));
  }, [raceId, userId]);

  const isMe = currentUser && String(currentUser.id) === String(userId);

  async function save() {
    const token = localStorage.getItem('token');
    try {
      // If no airportHotelId but the user has typed hotel fields, create the hotel first
      let finalHotelId = airportHotelId;
      const hasHotelFields = newHotel && (newHotel.name || newHotel.airport || newHotel.address || newHotel.check_in || newHotel.check_out);
      if (!finalHotelId && hasHotelFields) {
        const created = await createHotel();
        if (created && created.id) {
          finalHotelId = created.id;
          setAirportHotelId(created.id);
        }
      }

      const res = await fetch(`/api/races/${raceId}/airport_hotels/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ notes: notesText, airport_hotel_id: finalHotelId })
      });
      if (res.ok) {
        const data = await res.json();
        setNote(data);
        setEditing(false);
      } else {
        console.error('Save failed');
      }
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    if (airportHotelId) {
      const token = localStorage.getItem('token');
      fetch(`/api/airport_hotels/${airportHotelId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => setHotelDetail(d)).catch(() => setHotelDetail(null));
    } else {
      setHotelDetail(null);
    }
  }, [airportHotelId]);

  // seed editable fields from fetched hotelDetail when available
  useEffect(() => {
    if (hotelDetail && (!newHotel || !newHotel.name)) {
      setNewHotel({
        name: hotelDetail.name || '',
        airport: hotelDetail.airport || '',
        address: hotelDetail.address || '',
        check_in: hotelDetail.check_in || '',
        check_out: hotelDetail.check_out || ''
      });
    }
  }, [hotelDetail]);

  async function createHotel() {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/airport_hotels', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newHotel)
      });
      if (res.ok) {
        const data = await res.json();
        // refresh list
        setAirportList(prev => [data, ...prev]);
        setCreating(false);
        return data;
      } else {
        console.error('Failed to create hotel');
        return null;
      }
    } catch (e) { console.error(e); }
    return null;
  }

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Airport hotel — {note ? 'Entry' : 'No entry'}</h2>
        <Link to={`/races/${raceId}`} className="text-sm text-blue-600">Back to race</Link>
      </div>

      <div className="bg-white p-4 rounded shadow">
        <div className="mb-3">
          <strong>User:</strong> {isMe ? 'You' : `User ${userId}`}
        </div>

        <div className="mb-3">
          <label className="block text-sm">Airport hotel id (optional)</label>
          {isMe ? (
            <div className="flex gap-2 items-start">
              <select className="p-2 border flex-1" value={airportHotelId || ''} onChange={e => setAirportHotelId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">-- Select existing airport hotel --</option>
                {airportList.map(h => (
                  <option key={h.id} value={h.id}>{h.name} — {h.airport || ''}</option>
                ))}
              </select>
              <div className="flex flex-col gap-2">
                <button className="px-3 py-1 bg-gray-200 rounded" onClick={() => setCreating(c => !c)}>{creating ? 'Cancel' : 'Create new'}</button>
              </div>
            </div>
          ) : (
            <input className="p-2 border w-full" value={airportHotelId || ''} disabled />
          )}
        </div>

        {/* Hotel detail inputs: show inline so user can enter full hotel info without toggling 'Create new' */}
        {isMe && (creating || !airportHotelId) && (
          <div className="mb-3 bg-gray-50 p-3 rounded">
            <label className="block text-sm font-medium mb-2">Airport hotel details</label>
            <div className="grid grid-cols-1 gap-2">
              <input className="p-2 border" placeholder="Name" value={newHotel.name} onChange={e => setNewHotel(v => ({...v, name: e.target.value}))} />
              <input className="p-2 border" placeholder="Airport" value={newHotel.airport} onChange={e => setNewHotel(v => ({...v, airport: e.target.value}))} />
              <input className="p-2 border" placeholder="Address" value={newHotel.address} onChange={e => setNewHotel(v => ({...v, address: e.target.value}))} />
              <div className="flex gap-2">
                <input type="datetime-local" className="p-2 border flex-1" placeholder="Check in" value={newHotel.check_in} onChange={e => setNewHotel(v => ({...v, check_in: e.target.value}))} />
                <input type="datetime-local" className="p-2 border flex-1" placeholder="Check out" value={newHotel.check_out} onChange={e => setNewHotel(v => ({...v, check_out: e.target.value}))} />
              </div>
            </div>
          </div>
        )}

        <div className="mb-3">
          <label className="block text-sm">Notes</label>
          <textarea className="w-full p-2 border" rows={6} value={notesText} onChange={e => setNotesText(e.target.value)} disabled={!isMe} />
        </div>

        <div className="flex gap-2">
          {isMe ? (
            <>
              <button onClick={save} className="px-3 py-1 bg-blue-600 text-white rounded">Save</button>
              <button onClick={() => { setEditing(false); navigate(`/races/${raceId}`); }} className="px-3 py-1 border rounded">Cancel</button>
            </>
          ) : (
            <div className="text-sm text-gray-500">You can only edit your own airport hotel entry.</div>
          )}
        </div>
      </div>
      {hotelDetail && (
        <div className="mt-4 bg-white p-4 rounded shadow">
          <h3 className="font-semibold mb-2">Selected Airport Hotel</h3>
          <div><strong>{hotelDetail.name}</strong></div>
          <div className="text-sm text-gray-600">{hotelDetail.airport}</div>
          <div className="text-sm">{hotelDetail.address}</div>
          <div className="text-sm text-gray-500">{hotelDetail.check_in} → {hotelDetail.check_out}</div>
        </div>
      )}

      {creating && (
        <div className="mt-4 bg-white p-4 rounded shadow">
          <h3 className="font-semibold mb-2">Create new Airport Hotel</h3>
          <div className="grid grid-cols-1 gap-2">
            <input className="p-2 border" placeholder="Name" value={newHotel.name} onChange={e => setNewHotel(v => ({...v, name: e.target.value}))} />
            <input className="p-2 border" placeholder="Airport" value={newHotel.airport} onChange={e => setNewHotel(v => ({...v, airport: e.target.value}))} />
            <input className="p-2 border" placeholder="Address" value={newHotel.address} onChange={e => setNewHotel(v => ({...v, address: e.target.value}))} />
            <input type="datetime-local" className="p-2 border" placeholder="Check in" value={newHotel.check_in} onChange={e => setNewHotel(v => ({...v, check_in: e.target.value}))} />
            <input type="datetime-local" className="p-2 border" placeholder="Check out" value={newHotel.check_out} onChange={e => setNewHotel(v => ({...v, check_out: e.target.value}))} />
            <div className="flex gap-2">
              <button onClick={createHotel} className="px-3 py-1 bg-green-600 text-white rounded">Create</button>
              <button onClick={() => setCreating(false)} className="px-3 py-1 border rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
